import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { TableComponent } from '../../atoms/table/table.component';
import { TableColumn } from '../../atoms/table/table.model';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { RANKING_TSABTS_RSABT_MAX_INTENSITY_DEFAULT_COLUMNS } from '../../../constants/table.constants';
import { BadgeIndicatorComponent } from '../../molecules/badge-indicator/badge-indicator.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MaxIntensityRankingRsabt } from '@data';
import { TimeIndicatorComponent } from '../../molecules/indicators/time-indicator.component';
import { TsabtIndicatorComponent } from '../../molecules/indicators/tsabt-indicator.component';
import { PhaseIndicatorComponent } from '../../molecules/indicators/phase-indicator.component';
import { NzEmptyModule } from 'ng-zorro-antd/empty';

@Component({
  selector: 'ui-max-intensity-tsabts-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    TranslocoDirective,
    BadgeIndicatorComponent,
    TimeIndicatorComponent,
    TsabtIndicatorComponent,
    PhaseIndicatorComponent,
    NzEmptyModule,
  ],
  template: `
    <ng-container *transloco="let t">
      @if (dataSource().length && columns()!.length && columnTemplates()) {
        <ui-table
          [dataSource]="dataSource()"
          [columns]="columns()"
          [columnTemplates]="columnTemplates()"
          [showPagination]="showPagination()"
          [loading]="loading()"
        />
      } @else if (!loading()) {
        <nz-empty
          nzNotFoundImage="simple"
          [nzNotFoundContent]="emptyContentTemplate"
          class="py-8"
        ></nz-empty>
      }

      <ng-template #emptyContentTemplate>
        <span style="color: #737373; font-size: 0.875rem;">{{ t('CHARTS.VOID_TABLE_MESSAGE') }}</span>
      </ng-template>

      <ng-template #tsabtTemplate let-data="row">
        <ui-tsabt-indicator
          [showCenterCode]="showCenterCode()"
          [name]="data?.lineName"
          [lineName]="data?.lineName"
          [distributorR1]="data?.distributorR1"
          [distributorId]="data.distributorId"
          [rsabtId]="data.rsabtId"
          [centerId]="data.centerId"
          [vtnId]="data.vtnId"
          [tsabtId]="data.tsabtId"
          [lineId]="data.lineId"
          [centerName]="data.centerName"
          (onCenter)="emitCenter($event)"
        />
      </ng-template>

      <ng-template #maxIntensityTemplate let-data="row">
        <ui-badge-indicator
          [value]="data.intensity"
          [percentage]="data.intensityPercentage"
          [nominal]="data.intensityNominal"
          [units]="'A'"
          [tooltipLabel]="
            t('RSABT_MAX_INTENSITY.TOOLTIP_INTENSITY_TEMPLATE_LABEL')
          "
        />
      </ng-template>

      <ng-template #intensityPhaseTemplate let-data="row">
        <div class="flex justify-center w-full">
          <ui-phase-indicator [phase]="data.phase" />
        </div>
      </ng-template>

      <ng-template #timestampTemplate let-data="row">
        <ui-time-indicator [timestamp]="data.timestamp" />
      </ng-template>
    </ng-container>
  `,
})
export class MaxIntensityTsabtsTableComponent {
  DEFAULT_COLUMNS: TableColumn[] =
    RANKING_TSABTS_RSABT_MAX_INTENSITY_DEFAULT_COLUMNS;

  protected readonly translocoService = inject(TranslocoService);
  protected readonly destroyRef = inject(DestroyRef);

  data = input.required<MaxIntensityRankingRsabt[]>();
  loading = input<boolean>(false);
  showPagination = input<boolean>(false);
  showCenterCode = input<boolean>(false);

  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }>();
  onRsabtId = output<string>();

  columnTemplates = signal<{ [key: string]: any }>({});
  columns = signal<TableColumn[]>(this.DEFAULT_COLUMNS);

  dataSource = computed(() =>
    this.data().map((item) => {
      // try to use an explicit id if backend provides it
      const stableId =
        (item as any).id ??
        // fallback to a combination of stable fields (adjust fields to your model)
        `${item.centerId ?? ''}_${new Date(item.timestamp ?? '').getTime() || 0}_${item.rsabtId ?? ''}_${item.transformerId ?? ''}`;
      return { ...item, id: stableId };
    }),
  );

  @ViewChild('tsabtTemplate') tsabtTemplate!: ElementRef;
  @ViewChild('maxIntensityTemplate') maxIntensityTemplate!: ElementRef;
  @ViewChild('intensityPhaseTemplate') intensityPhaseTemplate!: ElementRef;
  @ViewChild('timestampTemplate') timestampTemplate!: ElementRef;

  columnTemplatesSet = false;

  constructor() {
    this.setLocalizeColumns();
  }

  ngAfterViewChecked(): void {
    if (
      this.tsabtTemplate &&
      this.maxIntensityTemplate &&
      this.intensityPhaseTemplate &&
      this.timestampTemplate &&
      !this.columnTemplatesSet
    ) {
      this.columnTemplatesSet = true;
      this.setColumnTemplates();
    }
  }

  setColumnTemplates() {
    this.columnTemplates.set({
      tsabt: this.tsabtTemplate,
      maxIntensity: this.maxIntensityTemplate,
      intensityPhase: this.intensityPhaseTemplate,
      timestamp: this.timestampTemplate,
    });
  }

  setLocalizeColumns() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns.set(
          this.DEFAULT_COLUMNS.map((col) => ({
            ...col,
            title: this.translocoService.translate(col.title),
          })),
        );
      });
  }

  emitRsabtId(id: string) {
    this.onRsabtId.emit(id);
  }
  emitCenter(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }) {
    this.onCenter.emit(event);
  }
}
